'use client';

import { useCallback, useEffect, useState, useMemo, startTransition } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import {
  AlertTriangleIcon,
  BagIcon,
  CrownIcon,
  FireIcon,
  GamepadIcon,
  GiftIcon,
  HeartIcon,
  MedalIcon,
  MinusIcon,
  PlusIcon,
} from '@/components/icons';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { ProductThumbImage } from '@/components/features/products/product-thumb-image';
import { 
  useCartStore,
  selectCartItems,
  selectMinOrderEligibleTotal,
  type CartItem,
  type BonusType,
  BONUS_STYLES,
  getBonusStyleForProduct,
} from '@/stores/cart-store';
import { useAddressStore, selectMinOrderSum, selectAddressHasHydrated } from '@/stores/address-store';
import { PromoCodeInput } from './promo-code-input';
import { StatusRollAutoAward } from './status-roll-auto-award';

// ========== TYPES ==========

interface CartItemsStepProps {
  onNext?: () => void;
}

// ========== HELPER FUNCTIONS ==========

const formatPrice = (price: number): string => {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
};

// Icon mapping for bonus types
const BONUS_ICONS: Record<BonusType, React.ElementType> = {
  first_order: HeartIcon,
  silver: MedalIcon,
  gold: CrownIcon,
  platinum: CrownIcon,
  promo: GiftIcon,
  roulette: GamepadIcon,
};

// Get badge style based on item type  
const getBadgeStyle = (item: CartItem, isDark: boolean) => {
  // Check if item has a bonus type
  const bonusType = item.bonusType || getBonusStyleForProduct(item.id)?.type;
  
  if (bonusType) {
    const style = BONUS_STYLES[bonusType];
    return {
      label: style.label,
      color: style.color,
      bgColor: isDark ? style.bgColorDark : style.bgColorLight,
      borderColor: isDark ? style.borderColorDark : style.borderColorLight,
      icon: BONUS_ICONS[bonusType] || GiftIcon,
      type: bonusType,
    };
  }
  
  // Fallback for legacy bonus items without bonusType
  if (item.isBonus) {
    const style = BONUS_STYLES.first_order;
    return {
      label: style.label,
      color: style.color,
      bgColor: isDark ? style.bgColorDark : style.bgColorLight,
      borderColor: isDark ? style.borderColorDark : style.borderColorLight,
      icon: HeartIcon,
      type: 'first_order' as BonusType,
    };
  }
  
  return null;
};

// ========== CART ITEM COMPONENT ==========

function CartItemCard({ 
  item, 
  isDark,
  onRemove,
  onUpdateQuantity,
}: { 
  item: CartItem;
  isDark: boolean;
  onRemove: (id: number) => void;
  onUpdateQuantity: (id: number, qty: number) => void;
}) {
  const badge = getBadgeStyle(item, isDark);
  const isSpecialItem = item.isBonus || !!item.bonusType;
  const canChangeQuantity = !isSpecialItem;
  const isPromoCodeItem = item.bonusType === 'promo';
  // All bonus/promo/status items are free
  // Промокод бывает платным («ролл по промокоду за 99 ₽») — тогда это обычная
  // оплачиваемая позиция, а не подарок. Проверяем только промо, чтобы не задеть
  // статусные роллы: у них в каталоге своя цена, но клиенту они достаются даром.
  const isPaidPromo = item.bonusType === 'promo' && Number(item.product.price) > 0;
  const isFreeItem = !isPaidPromo && (item.isBonus ||
    ['promo', 'silver', 'gold', 'platinum', 'first_order'].includes(item.bonusType || ''));
  
  // Get promo code name from localStorage for promo items
  const [promoCodeName, setPromoCodeName] = useState<string | null>(null);
  
  useEffect(() => {
    if (isPromoCodeItem) {
      const storedPromoCode = localStorage.getItem('appliedPromoCode');
      setPromoCodeName(storedPromoCode);
    }
  }, [isPromoCodeItem]);
  
  // Get border and background colors for special items
  const getSpecialItemStyles = () => {
    if (!badge) return {};
    
    // Special purple gradient style for promo code items
    if (isPromoCodeItem) {
      return {
        backgroundColor: isDark ? 'rgba(var(--bonus-promo-rgb), 0.15)' : 'var(--violet-50)',
        borderColor: isDark ? 'rgba(var(--bonus-promo-rgb), 0.3)' : 'var(--violet-200)',
      };
    }
    
    return {
      backgroundColor: badge.bgColor,
      borderColor: badge.borderColor,
    };
  };
  
  const specialStyles = isSpecialItem && badge ? getSpecialItemStyles() : {};
  const BadgeIcon = badge?.icon || GiftIcon;
  
  // Override badge color for promo items to purple
  const badgeColor = isPromoCodeItem ? 'var(--bonus-promo)' : badge?.color;
  const badgeLabel = isPromoCodeItem ? 'Промокод' : badge?.label;
  
  return (
    <motion.div
      layout
      layoutId={`cart-item-${item.id}-${item.bonusType || 'regular'}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className={cn(
        'relative p-3 rounded-card border mb-3 transition-colors',
        !isSpecialItem && (isDark 
          ? 'bg-[var(--surface-secondary)] border-[var(--border-default)]' 
          : 'bg-[var(--surface-primary)] border-[var(--border-default)]')
      )}
      style={isSpecialItem ? specialStyles : undefined}
    >
      {/* Badge */}
      {badge && (
        <motion.div 
          key={`badge-${item.id}-${badge.type}`}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
          className="absolute -top-2 -right-2 z-10"
        >
          <div 
            className="py-1 px-2.5 rounded-pill text-[10px] font-bold flex items-center gap-1 text-[var(--text-inverse)] shadow-raised"
            style={{ backgroundColor: badgeColor }}
          >
            <BadgeIcon weight="fill" className="w-3 h-3" />
            {badgeLabel}
          </div>
        </motion.div>
      )}
      
      <div className="flex items-center gap-3">
        {/* Image - Special gradient for promo code items */}
        <div className={cn(
          'relative w-16 h-16 rounded-card overflow-hidden flex-shrink-0',
          'bg-[var(--surface-tertiary)]'
        )}>
          {isPromoCodeItem ? (
            // Special gift icon for promo code items
            <div 
              className="w-full h-full flex items-center justify-center"
              style={{
                background: isDark 
                  ? 'var(--primary)' 
                  : 'var(--primary-subtle)',
              }}
            >
              <GiftIcon
                weight="fill" 
                className="w-8 h-8" 
                style={{ color: isDark ? 'var(--fuchsia-200)' : 'var(--bonus-promo)' }}
              />
            </div>
          ) : (
            <>
              <ProductThumbImage product={item.product} sizes="64px" />
              
              {/* Special item icon on image (not for promo items - they have gradient) */}
              {isSpecialItem && badge && (
                <motion.div
                  key={`icon-${item.id}-${badge.type}`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.05 }}
                  className="absolute top-1 right-1 p-1 rounded-pill"
                  style={{ backgroundColor: badge.color }}
                >
                  <BadgeIcon weight="fill" className="w-3 h-3 text-white" />
                </motion.div>
              )}
              
              {/* Quantity badge for regular items */}
              {item.quantity > 1 && !isSpecialItem && (
                <motion.div
                  key={`qty-${item.id}-${item.quantity}`}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  className="absolute top-1 right-1 w-5 h-5 rounded-pill bg-[var(--primary)] text-[var(--text-inverse)] text-[10px] font-bold flex items-center justify-center"
                >
                  {item.quantity}
                </motion.div>
              )}
            </>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className={cn(
            'font-medium text-sm truncate mb-1',
            'text-[var(--text-primary)]'
          )}>
            {isPromoCodeItem ? 'Подарок' : item.product.name}
          </h3>

          {(item.product as { is_set_of_the_day?: boolean }).is_set_of_the_day && (
            <p className="text-[11px] leading-tight text-[var(--text-tertiary)] mb-1.5">
              Не суммируется с промокодом и подарком за регистрацию
            </p>
          )}

          <div className="flex items-center justify-between">
            {/* Price / Promo subtitle */}
            {isFreeItem ? (
              <span 
                className="text-xs font-medium"
                style={{ 
                  color: isPromoCodeItem ? 'var(--bonus-promo)' : (badge?.color || 'var(--pink-500)')
                }}
              >
                {isPromoCodeItem && promoCodeName 
                  ? `по промокоду "${promoCodeName}"` 
                  : 'Подарок'}
              </span>
            ) : (
              <span className="flex items-baseline gap-2">
                <span className="text-sm font-semibold font-mono text-[var(--text-primary)]">
                  {formatPrice(item.product.price * item.quantity)}
                </span>
                {/* У платного промокода цена вытесняет подпись «по промокоду».
                    Без неё непонятно, откуда в корзине взялась эта позиция. */}
                {isPaidPromo && promoCodeName && (
                  <span
                    className="text-[11px] font-medium"
                    style={{ color: 'var(--bonus-promo)' }}
                  >
                    по промокоду «{promoCodeName}»
                  </span>
                )}
              </span>
            )}
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              {canChangeQuantity && (
                <div className={cn(
                  'flex items-center rounded-pill p-1',
                  isDark ? 'bg-[var(--surface-tertiary)]' : 'bg-[var(--violet-50)]'
                )}>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                    className={cn(
                      'p-1.5 rounded-pill transition-colors',
                      isDark ? 'text-[var(--primary)] hover:bg-[var(--surface-hover)]' : 'text-[var(--primary)] hover:bg-[var(--surface-primary)]',
                      item.quantity <= 1 && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <MinusIcon weight="bold" className="w-3 h-3" />
                  </button>
                  <span className={cn(
                    'w-6 text-center text-xs font-semibold',
                    isDark ? 'text-[var(--primary)]' : 'text-[var(--primary)]'
                  )}>
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    className={cn(
                      'p-1.5 rounded-pill transition-colors',
                      isDark ? 'text-[var(--primary)] hover:bg-[var(--surface-hover)]' : 'text-[var(--primary)] hover:bg-[var(--surface-primary)]'
                    )}
                  >
                    <PlusIcon weight="bold" className="w-3 h-3" />
                  </button>
                </div>
              )}
              
              <button
                onClick={() => onRemove(item.id)}
                className={cn(
                  'p-1.5 rounded-pill transition-colors',
                  isDark
                    ? 'text-[var(--text-tertiary)] hover:text-[var(--error)] hover:bg-[var(--error)]/10'
                    : 'text-[var(--text-tertiary)] hover:text-[var(--error)] hover:bg-[var(--error-bg)]'
                )}
              >
                <X weight="bold" className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ========== MIN ORDER ALERT ==========

function MinOrderAlert({ 
  currentTotal, 
  minOrder, 
  isDark 
}: { 
  currentTotal: number; 
  minOrder: number;
  isDark: boolean;
}) {
  const remaining = minOrder - currentTotal;
  const progress = Math.min(100, (currentTotal / minOrder) * 100);
  
  if (currentTotal >= minOrder) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'p-3 rounded-card mb-3',
        isDark
          ? 'bg-[var(--primary-subtle)] border border-[var(--primary)]/20'
          : 'bg-[var(--violet-50)] border border-[var(--primary)]/20'
      )}
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangleIcon weight="fill" className={cn(
          'w-5 h-5 flex-shrink-0',
          'text-[var(--primary)]'
        )} />
        <div className="flex-1">
          <p className={cn(
            'text-sm font-medium mb-2',
            'text-[var(--text-primary)]'
          )}>
            Минимальная сумма заказа: {formatPrice(minOrder)}
          </p>
          
          {/* Progress Bar */}
          <div className={cn(
            'h-2 rounded-hairline overflow-hidden mb-1',
            isDark ? 'bg-[var(--surface-tertiary)]' : 'bg-[var(--surface-primary)]'
          )}>
            <motion.div
              className="h-full bg-[var(--primary)]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <p className={cn(
              'text-xs',
              'text-[var(--text-secondary)]'
            )}>
              Добавьте ещё на {formatPrice(remaining)}
            </p>
            <p className={cn(
              'text-xs font-medium',
              'text-[var(--primary)]'
            )}>
              {Math.round(progress)}%
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ========== HOT ROLLS ALERT ==========

function HotRollsAlert({ isDark }: { isDark: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'p-3 rounded-card mb-3',
        isDark
          ? 'bg-[var(--warning)]/10 border border-[var(--warning)]/20'
          : 'bg-[var(--warning-bg)] border border-[var(--warning)]/20'
      )}
    >
      <div className="flex items-start gap-2.5">
        <FireIcon weight="fill" className="w-5 h-5 text-[var(--warning)] flex-shrink-0" />
        <div className="flex-1">
          <p className={cn(
            'text-sm font-medium',
            'text-[var(--text-primary)]'
          )}>
            Ваш заказ содержит горячие роллы
          </p>
          <p className={cn(
            'text-xs mt-1',
            isDark ? 'text-[var(--text-secondary)]' : 'text-[var(--text-secondary)]'
          )}>
            Температура может снизиться при доставке
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ========== EMPTY CART ==========

function EmptyCart({ isDark }: { isDark: boolean }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center p-8">
      <div className={cn(
        'w-20 h-20 rounded-pill flex items-center justify-center mb-4',
        isDark ? 'bg-[var(--surface-secondary)]' : 'bg-[var(--violet-50)]'
      )}>
        <BagIcon weight="fill" className={cn(
          'w-10 h-10',
          isDark ? 'text-[var(--primary)]' : 'text-[var(--primary)]'
        )} />
      </div>
      <p className={cn(
        'font-medium mb-2',
        'text-[var(--text-primary)]'
      )}>
        Ваша корзина пуста
      </p>
      <p className={cn(
        'text-sm',
        isDark ? 'text-[var(--text-secondary)]' : 'text-[var(--text-secondary)]'
      )}>
        Добавьте товары, чтобы оформить заказ
      </p>
    </div>
  );
}

// ========== MAIN COMPONENT ==========

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function CartItemsStep(_props: CartItemsStepProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  
  // Cart Store
  const cartItems = useCartStore(selectCartItems);
  // Сумма для проверки минимального заказа — без бесплатных соусов (id 1-7).
  const minOrderEligible = useCartStore(selectMinOrderEligibleTotal);
  const removeItem = useCartStore((state) => state.removeItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  
  // Address Store
  const addressHasHydrated = useAddressStore(selectAddressHasHydrated);
  const minOrderSumRaw = useAddressStore(selectMinOrderSum);
  const minOrderSum = useMemo(() => addressHasHydrated ? minOrderSumRaw : 0, [addressHasHydrated, minOrderSumRaw]);
  
  // Мемоизированные вычисления
  // «сет» из условия убран: под подстроку попадал ЛЮБОЙ сет, и человеку с
  // холодным сетом показывали «ваш заказ содержит горячие роллы» — прямо
  // против текста в карточке того же товара («ролл не является горячим»).
  // Горячее опознаём по способу приготовления, а не по типу позиции.
  const hasHotRolls = useMemo(() => cartItems.some(item => {
    const name = item.product?.name?.toLowerCase() || '';
    return name.includes('запеченный') ||
           name.includes('запечённый') ||
           name.includes('темпурный');
  }), [cartItems]);
  
  const regularItems = useMemo(() => 
    cartItems.filter(item => !item.isBonus && !item.bonusType),
    [cartItems]
  );
  const specialItems = useMemo(() => 
    cartItems.filter(item => item.isBonus || item.bonusType),
    [cartItems]
  );
  
  // Handlers с startTransition
  const handleRemove = useCallback((id: number) => {
    startTransition(() => {
      removeItem(id);
    });
  }, [removeItem]);
  
  const handleUpdateQuantity = useCallback((id: number, quantity: number) => {
    startTransition(() => {
      if (quantity <= 0) {
        removeItem(id);
      } else {
        updateQuantity(id, quantity);
      }
    });
  }, [removeItem, updateQuantity]);
  
  if (cartItems.length === 0) {
    return <EmptyCart isDark={isDark} />;
  }
  
  return (
    <div className="p-4">
      {/* Alerts */}
      {minOrderSum > 0 && minOrderEligible < minOrderSum && (
        <MinOrderAlert
          currentTotal={minOrderEligible}
          minOrder={minOrderSum}
          isDark={isDark}
        />
      )}
      
      {hasHotRolls && <HotRollsAlert isDark={isDark} />}
      
      {/* Cart Items */}
      <AnimatePresence mode="sync">
        {/* Regular Items */}
        {regularItems.map((item) => (
          <CartItemCard
            key={`regular-${item.id}`}
            item={item}
            isDark={isDark}
            onRemove={handleRemove}
            onUpdateQuantity={handleUpdateQuantity}
          />
        ))}
        
        {/* Special Items (Bonus, Daily Roll, Status Rolls) */}
        {specialItems.map((item) => (
          <CartItemCard
            key={`special-${item.id}-${item.bonusType || 'bonus'}`}
            item={item}
            isDark={isDark}
            onRemove={handleRemove}
            onUpdateQuantity={handleUpdateQuantity}
          />
        ))}
      </AnimatePresence>
      
      {/* Начисление статусного ролла. Ничего не рисует (шкалы убраны), но обязан
          быть смонтирован — вся механика начисления живёт в его эффекте.
          Обёртку с отступом убрали: пустой div давал лишние 16px в списке. */}
      {regularItems.length > 0 && <StatusRollAutoAward />}
      
      {/* Promo Code Input */}
      {regularItems.length > 0 && (
        <div className="mt-4">
          <PromoCodeInput />
        </div>
      )}
    </div>
  );
}

